import { useQuery } from "@apollo/client";
import { Box, LinearProgress, Typography } from "@mui/material";
import { sumBy } from "lodash";
import { ReactNode } from "react";

import {
  GetCampaignForViewQuery,
  ListTagsAndGroupsQuery,
  ListTagsAndGroupsQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LIST_TAGS_AND_GROUPS } from "~frontend/graphql/tags";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import formatTagName from "~frontend/util/formatTagName";

import { CampaignViewAccordion } from "./CampaignViewAccordion";

export const TagUsage = ({
  tagUsage,
}: {
  tagUsage: GetCampaignForViewQuery["getCampaignByShortId"]["tagUsage"];
}): ReactNode => {
  const { subaccount } = useSubaccount()!;
  const {
    data: tagList,
    loading,
    error,
  } = useQuery<ListTagsAndGroupsQuery, ListTagsAndGroupsQueryVariables>(
    LIST_TAGS_AND_GROUPS,
    {
      variables: {
        subaccountId: subaccount?.id,
      },
    }
  );

  const totalApplied = sumBy(tagUsage, "count");
  const hasTags =
    tagList &&
    (tagList.listTags.length > 0 || tagList.listTagGroups.length > 0);

  // Don't show the tag usage section if there are no tags available for this
  // subaccount or it is loading
  if ((loading || error || !hasTags) && totalApplied === 0) {
    return null;
  }

  if (totalApplied === 0 && hasTags) {
    return (
      <FlexBox
        sx={{ backgroundColor: palette.white, px: 7, py: 3 }}
        direction="column"
        gap={1}
      >
        <Typography variant="h3Bold">Tags</Typography>
        <Typography variant="body1">No tags have been applied yet.</Typography>
      </FlexBox>
    );
  }

  return (
    <CampaignViewAccordion
      header="Tags"
      secondHeader={`${totalApplied} Total Tags Applied`}
      sx={{ backgroundColor: palette.white, mt: 0 }}
    >
      <FlexBox direction="column" gap={2}>
        {tagUsage.map(tag => (
          <Box key={tag.tagId}>
            <Typography variant="body2">
              <strong>
                {formatTagName({
                  name: tag.name,
                  group: tag.groupName
                    ? {
                        name: tag.groupName,
                      }
                    : undefined,
                })}
              </strong>{" "}
              {tag.count}/{totalApplied}
            </Typography>
            <LinearProgress
              sx={{ height: 8 }}
              variant="determinate"
              value={(tag.count / totalApplied) * 100}
            />
          </Box>
        ))}
      </FlexBox>
    </CampaignViewAccordion>
  );
};
